import { useEffect, useState } from 'react';
import { navigationSections } from '../content';
import { MagneticCta } from '../components/MagneticCta';
import { siteConfig } from '../../../shared/config/site';
import { scrollToSection } from '../../../shared/lib/scroll';

export function HeaderSection() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleNavClick = (sectionId: string) => {
    setMenuOpen(false);
    scrollToSection(sectionId);
  };

  return (
    <header
      className={`sticky top-[var(--demo-banner-height)] z-50 border-b transition-colors duration-300 ${
        scrolled ? 'border-neutral-200 bg-[rgba(245,247,245,0.92)] backdrop-blur-md' : 'border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex max-w-[var(--container-max)] items-center justify-between px-[var(--section-padding-x)] py-4">
        <button
          type="button"
          onClick={() => scrollToSection('hero')}
          className="flex min-h-[44px] items-center font-display text-[1.0625rem] font-semibold tracking-tight text-ink sm:text-[1.125rem]"
          aria-label={`${siteConfig.shortName} home`}
        >
          {siteConfig.shortName}
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {navigationSections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => handleNavClick(section.id)}
              className="flex min-h-[44px] items-center px-2.5 font-body text-[0.8125rem] tracking-wide text-neutral-600 transition hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              {section.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <MagneticCta href={siteConfig.phoneHref} variant="primary">
            Call {siteConfig.phoneDisplay}
          </MagneticCta>
        </div>

        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center rounded-lg border border-neutral-300 text-ink transition hover:border-neutral-400 lg:hidden"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="sr-only">{menuOpen ? 'Close' : 'Menu'}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            {menuOpen ? (
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            ) : (
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          className="border-t border-neutral-200 bg-bone px-[var(--section-padding-x)] py-6 lg:hidden"
          aria-label="Mobile"
        >
          <ul className="flex flex-col gap-4">
            {navigationSections.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => handleNavClick(section.id)}
                  className="flex min-h-[44px] w-full items-center font-body text-lg text-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                >
                  {section.label}
                </button>
              </li>
            ))}
            <li className="pt-2">
              <MagneticCta href={siteConfig.phoneHref} variant="primary" className="w-full">
                Call {siteConfig.phoneDisplay}
              </MagneticCta>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}
